/**
 * Is the shell below its fold?
 *
 * `components.md` → Shell → The fold: below `--pp-shell-fold` the sidebar becomes a top
 * bar plus a drawer, and **the identity block moves to the top-right avatar and opens the
 * same menu downward, "so sign out is in exactly one place at every width."**
 *
 * ## Why this is JavaScript rather than two CSS-hidden copies
 *
 * The cheap way to move an element between two containers is to render it in both and
 * hide one with a media query. That would make the ruling's own sentence false in the one
 * way anything can check: **the DOM would contain two sign outs at every width**, and the
 * assertion that sign out exists exactly once — which has survived two redesigns of where
 * it lives — would start passing for the wrong reason or failing for a good one.
 *
 * So the host is chosen, not hidden. One `AccountMenu` exists at any width.
 *
 * ## The literal, again
 *
 * `1023.98px` is written here and in `shell.css`, and the two must agree. It cannot be
 * `var(--pp-shell-fold)` in either place: custom properties do not work in media-query
 * conditions, and `matchMedia` takes the same syntax. Measured rather than assumed. The
 * duplication is real and is recorded in the Drift Log rather than hidden behind a helper
 * that would only move it.
 *
 * ## The server snapshot is not decoration
 *
 * `apps/app`'s suite is `renderToStaticMarkup` by decision (c34), where `window` does not
 * exist. `useSyncExternalStore` throws under static rendering without a server snapshot,
 * and c37b is the entry about a gate that rendered nothing because module state was read
 * during render instead of subscribed to. **Desktop is the server answer**, so a static
 * render produces the sidebar layout — the one every existing static test asserts against.
 */
import { useSyncExternalStore } from "react";

/** The folded query, in the one syntax both CSS and `matchMedia` accept. */
const FOLDED_QUERY = "(max-width: 1023.98px)";

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const query = window.matchMedia(FOLDED_QUERY);
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}

function getSnapshot(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(FOLDED_QUERY).matches;
}

/** Static rendering has no viewport; the shell's unfolded form is the honest default. */
function getServerSnapshot(): boolean {
  return false;
}

export function useIsFolded(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
